import * as Sentry from '@sentry/react';
import React from 'react';

// Type declaration for Sentry loader script
declare global {
  interface Window {
    Sentry?: {
      init: (config: any) => void;
      onLoad?: (callback: (Sentry: any) => void) => void;
    };
  }
}

/** Session Replay records a masked page: no text, no input values, no images or media. */
export const REPLAY_PRIVACY = { maskAllText: true, maskAllInputs: true, blockAllMedia: true } as const;

/**
 * Replay options. ``stickySession: false`` keeps the replay session in memory
 * only: the SDK otherwise writes ``sentryReplaySession`` to sessionStorage,
 * which is device storage that is not strictly necessary (privacy policy §10).
 */
export const REPLAY_OPTIONS = { ...REPLAY_PRIVACY, stickySession: false } as const;

export const initSentry = async (dsn: string): Promise<void> => {
  // Determine environment from build-time variables or import.meta.env
  const environment = typeof __APP_ENVIRONMENT__ !== 'undefined'
    ? __APP_ENVIRONMENT__
    : import.meta.env.MODE || 'development';

  // Default: initialize from the BUNDLED @sentry/react SDK. We deliberately do
  // NOT depend on Sentry's remote loader script. When the Sentry server is
  // unreachable (e.g. a 502), loading a <script> from it fails and emits a
  // console error — which fails our smoke-test gate (any console error =
  // failure) and adds a doomed request on every page load. The bundled SDK is
  // already shipped in our app bundle, so a Sentry outage degrades gracefully:
  // events simply fail to *send* (silently dropped by the transport) instead of
  // breaking boot. Set VITE_SENTRY_USE_LOADER=true to opt back into the loader.
  const useLoaderScript = import.meta.env.VITE_SENTRY_USE_LOADER === 'true';

  if (useLoaderScript) {
    const { hostname: host, username: publicKey } = new URL(dsn);
    if (host && publicKey) {
      await new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = `https://${host}/js-sdk-loader/${publicKey}.min.js`;
        script.crossOrigin = 'anonymous';
        script.async = true;
        const done = () => { initWithNpmPackage(dsn, environment); resolve(); };
        script.onload = () => setTimeout(done, 100);
        // Loader unreachable (Sentry down) — fall back silently to the bundled
        // SDK. No console.error: a Sentry outage must not break the app/CI.
        script.onerror = done;
        document.head.appendChild(script);
      });
      return;
    }
  }

  initWithNpmPackage(dsn, environment);
};

const initWithNpmPackage = (dsn: string, environment: string) => {
  try {
    Sentry.init({
    dsn,
    tracesSampleRate: 1.0,
    // Replay only when an error occurs, never for ordinary sessions (privacy policy §6),
    // and only after the user opts in: the replay integration is NOT installed
    // here. ``enableSessionRecording()`` adds it when the user ticks "Enable
    // session recording" at login (or starts it from the topbar). The rates
    // below are read by the integration when it is added.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    environment,
    // Disable profiling in development to avoid document policy violations
    profilesSampleRate: environment === 'development' ? 0 : 1.0,
    // (Unhandled promise rejections + uncaught exceptions are captured by
    // default via Sentry's global handlers integration in SDK v10.)
    beforeSend(event, hint) {
      // Log to console in development for debugging
      if (environment === 'development') {
        console.log('[Sentry] Attempting to send event:', event.type || 'unknown');
      }
      // Don't filter out any events - send everything
      return event;
    },
    // Use default transport - custom transport removed to avoid conflicts
    // CORS errors will be logged via beforeSend and error handlers
    tracePropagationTargets: [
      'localhost', 
      'dev-api.clearminds.se',
      /^https:\/\/dev-api\.nodus\.foo\/api/
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      // Browser profiling requires the ``Document-Policy:
      // js-profiling=*`` response header, set in
      // ``frontend/public/_headers`` for Cloudflare Pages. The
      // <meta> tag form does NOT work — the policy spec forbids it.
      Sentry.browserProfilingIntegration(),
    ],
    });
  } catch {
    // Never let Sentry initialization break the app — if Sentry is down or
    // misconfigured, the app must keep working without error tracking.
  }
};

// The replay integration, created on first opt-in. The SDK allows only one
// instance per page, so it is created once and then started/stopped.
let replay: ReturnType<typeof Sentry.replayIntegration> | null = null;

/**
 * Start Session Replay (on-error buffer mode) for this page session. Called
 * only when the user opts in. Never throws: Sentry must not break the app.
 */
export const enableSessionRecording = () => {
  try {
    if (!Sentry.getClient()) return;
    if (!replay) {
      replay = Sentry.replayIntegration(REPLAY_OPTIONS);
      // Installing the integration starts buffering per replaysOnErrorSampleRate.
      Sentry.addIntegration(replay);
    } else {
      replay.startBuffering();
    }
    Sentry.addBreadcrumb({ category: 'session', message: 'Session recording enabled', level: 'info' });
  } catch {
    // ignore
  }
};

/** Stop Session Replay; the buffered (unsent) recording is discarded. */
export const disableSessionRecording = () => {
  try {
    if (replay) void replay.stop().catch(() => {});
    Sentry.addBreadcrumb({ category: 'session', message: 'Session recording disabled', level: 'info' });
  } catch {
    // ignore
  }
};

export const pauseSessionRecording = disableSessionRecording;
export const resumeSessionRecording = enableSessionRecording;
export const stopSessionRecording = disableSessionRecording;

export const trackPageView = (url: string, title: string) => {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Page view: ${title}`,
    data: { url, title },
    level: 'info'
  });
};

const FallbackComponent = () => {
  return React.createElement('div', {
    style: {
      display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: 24, background: 'var(--mantine-color-body, #f8f9fa)',
    },
  }, React.createElement('div', {
    style: {
      width: '100%', maxWidth: 420, padding: 32, display: 'flex', flexDirection: 'column' as const,
      gap: 16, background: 'var(--mantine-color-default, #fff)',
      borderRadius: 'var(--mantine-radius-lg, 12px)',
      boxShadow: 'var(--mantine-shadow-md, 0 4px 12px rgba(0,0,0,.1))',
      border: '1px solid var(--mantine-color-default-border, #dee2e6)',
      textAlign: 'center' as const,
    },
  }, [
    React.createElement('img', {
      key: 'logo', src: '/assets/nodus_logo-128.png', alt: 'Nodus',
      style: { width: 56, height: 56, margin: '0 auto', objectFit: 'contain' as const },
    }),
    React.createElement('h2', {
      key: 'title',
      style: { fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--mantine-color-red-6, #e03131)' },
    }, "Something went wrong"),
    React.createElement('p', {
      key: 'message',
      style: { fontSize: '0.875rem', color: 'var(--mantine-color-dimmed, #868e96)', margin: 0 },
    }, "An error occurred and has been reported to our team."),
    React.createElement('button', {
      key: 'button',
      style: {
        padding: '10px 20px', fontSize: '0.875rem', fontWeight: 500,
        borderRadius: 'var(--mantine-radius-md, 8px)', border: 'none', cursor: 'pointer',
        background: 'var(--mantine-primary-color-filled, #228be6)', color: '#fff',
      },
      onClick: () => window.location.reload(),
    }, "Refresh the page"),
  ]));
};

export const setUserContext = (user: { id: string | number } | null) => {
  if (user) {
    // The user id only: no email in Sentry (privacy policy §6).
    Sentry.setUser({ id: String(user.id) });

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Authenticated user',
      level: 'info',
    });
  } else {
    Sentry.setUser(null);
    
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User logged out',
      level: 'info',
    });
  }
};

export const setCustomerContext = (customer: { id: string; name: string } | null) => {
  if (customer) {
    Sentry.setTag('customerId', customer.id);
    Sentry.setTag('customerName', customer.name);
    
    Sentry.setContext('customer', {
      id: customer.id,
      name: customer.name,
    });
    
    Sentry.addBreadcrumb({
      category: 'customer',
      message: `Selected customer: ${customer.name}`,
      level: 'info',
      data: {
        customerId: customer.id
      }
    });
  } else {
    Sentry.setTag('customerId', null);
    Sentry.setTag('customerName', null);
    Sentry.setContext('customer', null);
  }
};

export const captureException = (error: any, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    contexts: context ? { additionalContext: context } : undefined
  });
};

export const SentryErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  return React.createElement(
    Sentry.ErrorBoundary,
    { 
      fallback: React.createElement(FallbackComponent),
      showDialog: true
    },
    children
  );
};

export default Sentry;
